// Path-based SPA router, ported from d3-auth's web/lib/router.js with two fixes
// noted at their sites below.
//
// The matching logic lives in route-match.js because this module instantiates a
// Router at import time, which touches window — so this file can never be
// imported by a bun test, and anything worth testing has to live next door.

import { matchRoute, ROUTES } from './route-match.js';

class Router {
    constructor(routes = ROUTES) {
        this.routes = routes;
        /** @type {string|null} */
        this.currentRoute = null;
        /** @type {{[key: string]: string}} */
        this.params = {};
        /** @type {object} */
        this.meta = {};
        /** @type {Set<Function>} */
        this.listeners = new Set();

        window.addEventListener('popstate', () => this.handleRoute());

        document.addEventListener('click', (e) => {
            // FIX vs d3-auth: it reads `e.target.closest('a')`. Shadow DOM
            // retargets events crossing a shadow boundary, so by the time a click
            // inside a component reaches this document listener, e.target is the
            // host element, not the <a> — and every in-component link silently
            // falls through to a full page load. composedPath()[0] is the real
            // innermost target.
            const path = e.composedPath();
            const source = /** @type {Element} */ (path[0]);
            const link = source?.closest?.('a');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href) return;

            if (
                link.target === '_blank' ||
                link.hasAttribute('download') ||
                href.startsWith('http') ||
                href.startsWith('//')
            ) {
                return;
            }

            if (href.startsWith('/')) {
                e.preventDefault();
                this.navigate(href);
            }
        });
    }

    /**
     * @param {string} path
     * @param {{replace?: boolean}} [options] replace swaps the current history
     *   entry rather than adding one — for a redirect, so Back skips it.
     */
    navigate(path, { replace = false } = {}) {
        if (!path.startsWith('/')) {
            path = `/${path}`;
        }
        if (replace) {
            window.history.replaceState({}, '', path);
        } else {
            window.history.pushState({}, '', path);
        }
        this.handleRoute();
    }

    getPath() {
        return window.location.pathname || '/';
    }

    getQuery() {
        return new URLSearchParams(window.location.search);
    }

    handleRoute() {
        const matched = matchRoute(this.routes, this.getPath());

        // FIX vs d3-auth: it redirects any unmatched path to '/'. Here that would
        // take someone whose invite link is a character off — the one audience
        // who has no account and cannot sign in — and drop them on the staff
        // sign-in screen with no explanation. An unmatched path renders not-found.
        this.currentRoute = matched ? matched.name : 'not-found';
        this.params = matched ? matched.params : {};
        this.meta = matched ? matched.meta : {};
        this.notify();
    }

    subscribe(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }

    notify() {
        const state = {
            route: this.currentRoute,
            params: this.params,
            path: this.getPath(),
            meta: this.meta,
        };
        this.listeners.forEach((fn) => {
            fn(state);
        });
    }
}

// Routes come from route-match.js so route-match.test.js asserts the real table.
export const router = new Router();
